import {
    Card,
    CardHeader,
    Dialog,
    DialogBody,
    DialogSurface,
    DialogTitle,
    makeStyles,
    shorthands,
    Text,
    Tooltip,
    Dropdown,
    Option,
} from '@fluentui/react-components';
import React, { useState } from 'react';
import { IChatMessage } from '../../../libs/models/ChatMessage';
import { customTokens } from '../../../styles';
import { FileDownloadListItem } from './FileDownloadListItem';
import { useFileDownloadService } from '../../../libs/hooks/useFileDownloadService';
import { getFileIcon } from '../../icons/DocumentIcon';
import { PDFViewer } from '../../fileviewer/PDFViewer';
import type { Citation } from '../../../libs/models/ChatMessage';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexWrap: 'wrap',
        ...shorthands.gap(customTokens.spacingVerticalS),
        flexDirection: 'row',
    },
    card: {
        display: 'flex',
        width: 'calc(25% - 16px)',
        height: 'fit-content',
        margin: '8px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    },
    fileCard: {
        backgroundColor: '#E8F5E9',
    },
    linkCard: {
        backgroundColor: '#E3F2FD',
    },
    link: {
        color: 'blue',
        textDecoration: 'underline',
        cursor: 'pointer',
    },
    divider: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyItems: 'center',
        minHeight: '96px',
        backgroundColor: customTokens.colorNeutralBackground1,
    },
    dropdown: {
        marginTop: '8px',
        width: '100%',
    },
});

enum FileAction {
    Download = 'download',
    OpenInBrowser = 'openInBrowser',
    OpenInEditor = 'openInEditor',
}

interface IResourceCardsProps {
    message: IChatMessage;
}

// ...existing imports...

export const ResourceCards: React.FC<IResourceCardsProps> = ({ message }) => {
    const classes = useClasses();
    const fileDownloadService = useFileDownloadService();
    const [editorBlob, setEditorBlob] = useState<Blob | null>(null);
    const [editorDialogOpen, setEditorDialogOpen] = useState(false);
    const [selectedActions, setSelectedActions] = useState<Record<number, FileAction>>({});

    if (!message.citations?.length) {
        return null;
    }

        const handleAction = async (
        citation: Citation,
        fileName: string | null,
        action: FileAction,
        _index: number // unused, so prefixed with _
    ) => {
        if (!fileDownloadService) {
            console.warn('FileDownloadService is not available.');
            return;
        }
        try {
            const blobResult = await fileDownloadService.getFileAsBlob(citation.link);

            if (!(blobResult instanceof Blob)) {
                throw new Error('Downloaded file is not a Blob.');
            }
            if (action === FileAction.Download) {
                const url = window.URL.createObjectURL(blobResult);
                const anchor = document.createElement('a');
                anchor.href = url;
                anchor.download = fileName ?? 'downloaded_file';
                document.body.appendChild(anchor);
                anchor.click();
                document.body.removeChild(anchor);
                window.URL.revokeObjectURL(url);
            } else if (action === FileAction.OpenInBrowser) {
                const url = window.URL.createObjectURL(blobResult);
                window.open(url, '_blank');
                setTimeout(() => { window.URL.revokeObjectURL(url); }, 10000);
            } else {
                setEditorBlob(blobResult);
                setEditorDialogOpen(true);
            }
        } catch (error: unknown) {
            if (error instanceof Error) {
                console.error('File action failed:', error.message);
            } else {
                console.error('File action failed with unknown error:', String(error));
            }
        }
    };

    return (
        <div className={classes.root}>
            {message.citations.map((citation, index) => {
                const isFileDownload = citation.link.startsWith('/download?');
                const fileName = isFileDownload
                    ? decodeURIComponent(
                          new URLSearchParams(citation.link.split('?')[1]).get('filename')?.split('/').pop() ??
                              'downloaded_file',
                      )
                    : null;
                const sourceName = citation.sourceName || 'External Link';
                const shortenedSourceName =
                    sourceName.length > 20
                        ? `${(sourceName.split('/').pop() ?? '').substring(0, 20)}...`
                        : sourceName;

                // const defaultAction =
                //     fileName?.toLowerCase().endsWith('.pdf')
                //         ? FileAction.OpenInBrowser
                //         : FileAction.Download;
                const defaultAction = FileAction.OpenInBrowser;
                const selectedAction = selectedActions[index] ?? defaultAction;

                return (
                    <Card
                        appearance={isFileDownload ? 'filled-alternative' : 'filled'}
                        size="small"
                        key={`citation-card-${index}`}
                        className={isFileDownload ? classes.fileCard : classes.linkCard}
                    >
                        <CardHeader
                            header={
                                isFileDownload ? (
                                    <div>
                                        <FileDownloadListItem
                                            fileName={fileName ?? 'downloaded_file'}
                                            onDownload={() => { void handleAction(citation, fileName, selectedAction, index); }}
                                        />
                                        <Dropdown
                                            style={{ display: 'none' }}
                                            className={classes.dropdown}
                                            value={selectedAction}
                                            onOptionSelect={(_, data) => {
                                                setSelectedActions((prev) => ({
                                                    ...prev,
                                                    [index]: data.optionValue as FileAction,
                                                }));
                                            }}
                                        >
                                            <Option value={FileAction.Download}>Download</Option>
                                            <Option value={FileAction.OpenInBrowser}>Open in Browser</Option>
                                            <Option value={FileAction.OpenInEditor}>Open in Editor</Option>
                                        </Dropdown>
                                    </div>
                                ) : (
                                    <Tooltip content={sourceName} relationship="label">
                                        <Text weight="semibold">
                                            <span style={{ marginRight: '8px' }}>{getFileIcon('link.html')}</span>
                                            <a
                                                href={citation.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={classes.link}
                                            >
                                                {shortenedSourceName}
                                            </a>
                                        </Text>
                                    </Tooltip>
                                )
                            }
                        />
                    </Card>
                );
            })}
            <Dialog open={editorDialogOpen} onOpenChange={(_, data) => { setEditorDialogOpen(!!data.open); }}>
                <DialogSurface style={{ width: '80vw', height: '90vh' }}>
                    <DialogBody>
                        <DialogTitle>File Editor</DialogTitle>
                        {editorBlob && <PDFViewer pdfBlob={editorBlob} />}
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </div>
    );
};